<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Doughnut } from 'vue-chartjs';
import { Chart as ChartJS, Tooltip, ArcElement } from 'chart.js';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import { formatNumber } from 'kanban/helper/reportFormatters';

// Distribuicao da base por segmento RFM. Paleta categorica em ordem FIXA por
// segmento (validada p/ CVD, claro+escuro); a legenda com valores ao lado e o
// "relief" exigido pelo contraste no modo claro — identidade nunca so por cor.
const props = defineProps({
  // { rows: [{ segment, count, total_monetary }], total_customers }
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

ChartJS.register(Tooltip, ArcElement);

const { t, locale } = useI18n();

// Ordem fixa de segmentos -> slot de cor (nunca por rank/ordem de chegada)
const SEGMENT_ORDER = [
  'Champions',
  'Loyal',
  'Potential',
  'New',
  'At-Risk',
  "Can't-Lose",
  'Hibernating',
];
const LIGHT_COLORS = [
  '#2a78d6',
  '#008300',
  '#e87ba4',
  '#eda100',
  '#1baf7a',
  '#eb6834',
  '#4a3aa7',
];
const DARK_COLORS = [
  '#3987e5',
  '#008300',
  '#d55181',
  '#c98500',
  '#199e70',
  '#d95926',
  '#9085e9',
];

const isDark = computed(() =>
  document.documentElement.classList.contains('dark')
);
const palette = computed(() => (isDark.value ? DARK_COLORS : LIGHT_COLORS));
const surface = computed(() => (isDark.value ? '#151718' : '#fcfcfb'));

const rows = computed(() => {
  const list = props.data?.rows || [];
  return SEGMENT_ORDER.map((segment, index) => ({
    segment,
    index,
    count: list.find(row => row.segment === segment)?.count || 0,
  })).filter(row => row.count > 0);
});

const isEmpty = computed(() => !rows.value.length);

const segmentLabel = segment =>
  t(
    `VINCTO_HOME.SEGMENTS.NAMES.${segment.replace(/[^a-zA-Z]/g, '_').toUpperCase()}`
  );

const collection = computed(() => ({
  labels: rows.value.map(row => segmentLabel(row.segment)),
  datasets: [
    {
      data: rows.value.map(row => row.count),
      backgroundColor: rows.value.map(row => palette.value[row.index]),
      // gap de 2px entre fatias adjacentes (spacer sobre a superficie)
      borderColor: surface.value,
      borderWidth: 2,
    },
  ],
}));

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '68%',
  animation: { duration: 0 },
  plugins: {
    legend: { display: false },
    tooltip: { displayColors: false },
  },
};
</script>

<template>
  <KanbanReportWidget
    class="!rounded-2xl !border-n-weak !bg-white dark:!bg-n-solid-2"
    :title="t('VINCTO_HOME.SEGMENTS.TITLE')"
    :info-text="t('VINCTO_HOME.SEGMENTS.INFO')"
    :is-loading="isLoading"
    :is-empty="isEmpty"
    :empty-text="t('VINCTO_HOME.SEGMENTS.EMPTY')"
  >
    <div class="flex items-center gap-6">
      <div class="relative h-48 w-48 flex-shrink-0">
        <Doughnut :data="collection" :options="chartOptions" />
        <div
          class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
        >
          <span class="text-2xl font-medium text-n-slate-12">
            {{ formatNumber(data?.total_customers, locale) }}
          </span>
          <span class="text-xs text-n-slate-10">
            {{ t('VINCTO_HOME.SEGMENTS.CUSTOMERS') }}
          </span>
        </div>
      </div>
      <ul class="flex flex-col gap-1.5 flex-1 min-w-0">
        <li
          v-for="row in rows"
          :key="row.segment"
          class="flex items-center gap-2 text-sm"
        >
          <span
            class="w-2.5 h-2.5 rounded-sm flex-shrink-0"
            :style="{ backgroundColor: palette[row.index] }"
          />
          <span class="flex-1 text-n-slate-11 truncate">
            {{ segmentLabel(row.segment) }}
          </span>
          <span class="font-medium text-n-slate-12">
            {{ formatNumber(row.count, locale) }}
          </span>
        </li>
      </ul>
    </div>
  </KanbanReportWidget>
</template>
